import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function CaseStudyPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = t('seo.caseStudy.title');
    const description = t('seo.caseStudy.description');

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', t('seo.caseStudy.keywords'));
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}/ota-city-case-study`);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}/ota-city-case-study`);
    setMeta('meta[name="last-modified"]', 'content', '2025-07-14');

    const schemaId = 'schema-case-study';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      url: `${SITE_URL}/ota-city-case-study`,
      datePublished: '2025-01-01',
      dateModified: '2025-07-14',
      author: {
        '@type': 'Organization',
        name: 'Magome Kominka',
        url: `${SITE_URL}/`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Magome Kominka',
        url: `${SITE_URL}/`,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/ota-city-case-study`,
      },
      about: [
        { '@type': 'Thing', name: 'Minpaku Registration Tokyo' },
        { '@type': 'Thing', name: 'Ryokan License Tokyo' },
        { '@type': 'Thing', name: 'Airbnb Management Tokyo' },
        { '@type': 'Place', name: 'Ota City, Tokyo' },
      ],
    });

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [i18n.language, t]);

  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string }>({});
  const navigate = useNavigate();

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (form: HTMLFormElement): boolean => {
    const data = new FormData(form);
    const name = (data.get('name') as string || '').trim();
    const email = (data.get('email') as string || '').trim();
    const errors: { name?: string; email?: string } = {};
    if (!name) {
      errors.name = 'お名前を入力してください';
    }
    if (!email) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = '正しいメールアドレスの形式で入力してください';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!validateForm(form)) return;
    setFormState('submitting');
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d6h8ag4thj2bbt0jp84g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        navigate('/thank-you');
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Serif JP', serif" }}>
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-36 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0a00 0%, #3d1a00 40%, #5c2a00 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c441b20d824f38816143015dc10df040.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/55" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#C8960C]/20 border border-[#C8960C]/40 text-[#C8960C] text-xs font-bold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase">
            <i className="ri-building-line"></i>
            Real Case Study · Ota City, Tokyo
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Ota City Minpaku
            <span className="block text-[#C8960C] mt-2 text-3xl md:text-4xl">
              Launch Case Study
            </span>
          </h1>
          <p className="text-sm leading-relaxed text-white/80 max-w-3xl mx-auto mb-8">
            A detailed, real-world account of how we guided a property owner through the complete
            journey — from initial consultation to first Airbnb booking — in{' '}
            <strong className="text-white font-bold">Ota City, Tokyo</strong>.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-white/70">
            <span className="flex items-center gap-1.5"><i className="ri-map-pin-line text-[#C8960C]"></i> Ota City, Tokyo</span>
            <span className="flex items-center gap-1.5"><i className="ri-home-4-line text-[#C8960C]"></i> Detached House · 2LDK</span>
            <span className="flex items-center gap-1.5"><i className="ri-time-line text-[#C8960C]"></i> 10 min read</span>
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line text-[#C8960C]"></i> 2025 Case</span>
          </div>
        </div>
      </section>

      {/* Quick Stats Bar */}
      <section className="bg-[#F5F1E8] border-b border-[#C8960C]/20 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-calendar-check-line', value: '3.5 Months', label: 'Application to First Booking' },
              { icon: 'ri-money-yen-circle-line', value: '¥180,000+', label: 'Avg. Monthly Revenue' },
              { icon: 'ri-percent-line', value: '78%', label: 'Average Occupancy Rate' },
              { icon: 'ri-award-line', value: 'Ryokan', label: 'License Type Obtained' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-11 h-11 flex items-center justify-center mx-auto mb-3 rounded-xl" style={{ background: 'rgba(139,37,0,0.08)' }}>
                  <i className={`${stat.icon} text-xl text-[#8B2500]`}></i>
                </div>
                <div className="text-2xl font-bold text-[#8B2500]">{stat.value}</div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-10 border-b border-[#C8960C]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xs font-bold text-[#8B2500] uppercase tracking-widest mb-4">Case Study Contents</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { num: '01', label: 'Background', href: '#background' },
              { num: '02', label: 'Challenges', href: '#challenges' },
              { num: '03', label: 'Our Process', href: '#process' },
              { num: '04', label: 'Launch Timeline', href: '#timeline' },
              { num: '05', label: 'Revenue Model', href: '#revenue' },
              { num: '06', label: 'Key Takeaways', href: '#takeaways' },
            ].map((item) => (
              <a
                key={item.num}
                href={item.href}
                className="flex items-center gap-3 bg-[#F5F1E8] border border-[#C8960C]/20 rounded-lg px-4 py-3 hover:-translate-y-2 hover:shadow-lg hover:border-[#8B2500] transition-all duration-300 cursor-pointer group"
              >
                <span className="text-xs font-bold text-[#C8960C] group-hover:text-[#8B2500]">{item.num}</span>
                <span className="text-sm text-[#3d1f0a] leading-tight">{item.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">

          {/* ── Section 1: Background ── */}
          <section id="background">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white flex-shrink-0">
                <i className="ri-user-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">Section 01</span>
                <h2 className="text-3xl font-bold text-[#8B2500]">Background</h2>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div
                className="md:col-span-1 rounded-xl overflow-hidden"
                style={{ height: '260px' }}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=traditional%20Japanese%20two-story%20detached%20house%20Ota%20City%20Tokyo%20residential%20street%20clean%20exterior%20wooden%20facade%20warm%20afternoon%20light%20simple%20garden&width=400&height=520&seq=case-bg-property-01&orientation=portrait"
                  alt="Ota City property exterior"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="md:col-span-2 space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The client — a Japanese national in their early 50s — owned a detached two-story house
                  (2LDK, approximately 65 m²) in a quiet residential area of Ota City, Tokyo, roughly
                  10 minutes by train from Haneda Airport. The property had been vacant for over a year
                  following a family relocation, and the owner was exploring options to generate income
                  without selling.
                </p>
                <p>
                  After researching short-term rental platforms, the owner identified Airbnb as the most
                  viable income channel — particularly given Ota City's proximity to Haneda and its
                  designation as a <strong className="text-[#8B2500]">National Strategic Special Zone</strong>,
                  which permits more flexible short-term rental operations than most other Tokyo wards.
                </p>
                <p>
                  The owner's primary goal was to generate a reliable side income of ¥150,000–¥200,000
                  per month while keeping personal involvement minimal. They were not interested in
                  day-to-day management and wanted a fully outsourced solution — from licensing through
                  to ongoing <strong className="text-[#8B2500]">Airbnb management in Tokyo</strong>.
                </p>
              </div>
            </div>

            {/* Property Profile Card */}
            <div className="bg-[#F5F1E8] border border-[#C8960C]/30 rounded-xl p-6">
              <h3 className="font-bold text-[#8B2500] mb-4 flex items-center gap-2">
                <i className="ri-home-4-line text-[#C8960C]"></i>
                Property Profile
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Location', value: 'Ota City, Tokyo' },
                  { label: 'Property Type', value: 'Detached House (2LDK)' },
                  { label: 'Floor Area', value: 'Approx. 65 m²' },
                  { label: 'Access', value: '10 min from Haneda' },
                  { label: 'Owner Goal', value: 'Passive Side Income' },
                  { label: 'Management', value: 'Fully Outsourced' },
                  { label: 'Target Revenue', value: '¥150,000–¥200,000/mo' },
                  { label: 'License Path', value: 'Ryokan (Simple Lodging)' },
                ].map((item) => (
                  <div key={item.label} className="bg-white rounded-lg p-3 border border-[#C8960C]/15">
                    <div className="text-xs text-gray-400 mb-1">{item.label}</div>
                    <div className="text-sm font-semibold text-[#3d1f0a]">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Section 2: Challenges ── */}
          <section id="challenges">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white flex-shrink-0">
                <i className="ri-error-warning-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">Section 02</span>
                <h2 className="text-3xl font-bold text-[#8B2500]">Challenges</h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-8">
              <p className="text-sm leading-relaxed">
                While the property's location in Ota City was advantageous, the path to a legal,
                operational short-term rental was far from straightforward. The owner faced a combination
                of regulatory, structural, and timeline challenges that are typical of first-time applicants
                pursuing <strong>Tokyo Minpaku support</strong> — and which underscore why professional
                guidance is so valuable.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: 'ri-government-line',
                  title: 'Licensing Complexity',
                  color: '#8B2500',
                  desc: 'The owner initially assumed a simple Minpaku notification would suffice. After our initial assessment, it became clear that the Ryokan (Simple Lodging) license was the correct path — given the goal of year-round operation and full management outsourcing. The Ryokan application process is significantly more complex, involving multiple government agencies, a formal on-site inspection, and comprehensive fire safety compliance. The owner had no prior experience with Japanese government licensing procedures.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Compliance Concerns',
                  color: '#8B2500',
                  desc: 'The property had not been used commercially before, and several structural elements required upgrading to meet Ryokan license standards. The fire safety assessment revealed the need for additional smoke detectors, a fire extinguisher, emergency lighting in the stairwell, and evacuation signage in both Japanese and English. The building\'s electrical panel also required inspection to confirm compliance with commercial-use standards. Each of these items needed to be resolved before the formal application could be submitted.',
                },
                {
                  icon: 'ri-time-line',
                  title: 'Timeline Pressure',
                  color: '#8B2500',
                  desc: 'The owner had a clear financial motivation to begin generating income as quickly as possible — the property had been vacant for over a year, and carrying costs (property tax, maintenance) were accumulating. There was also a strategic timing consideration: launching before the peak spring travel season (March–May) would significantly impact first-year revenue. This created pressure to move efficiently through each stage of the licensing process without cutting corners.',
                },
                {
                  icon: 'ri-translate-2',
                  title: 'Language & Documentation Barriers',
                  color: '#8B2500',
                  desc: 'All official documents — application forms, floor plans, operational procedures, emergency response protocols — must be submitted in Japanese. The owner had limited experience with formal Japanese bureaucratic language, and several required documents (building registry, fire safety certificate) needed to be obtained from separate government offices. Coordinating these across multiple agencies while maintaining accuracy and completeness was a significant logistical challenge.',
                },
                {
                  icon: 'ri-settings-3-line',
                  title: 'Operational Setup',
                  color: '#8B2500',
                  desc: 'Beyond licensing, the property needed to be fully prepared for guest use: professional photography, Airbnb listing creation and optimization, pricing strategy, cleaning protocols, key exchange systems, and guest communication templates. The owner wanted all of this handled without personal involvement — requiring a trusted, experienced partner rather than a simple administrative service.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex gap-5 bg-white border border-[#C8960C]/20 rounded-xl p-6 hover:-translate-y-2 hover:shadow-lg hover:border-[#8B2500] transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-[#FFF5E6] rounded-xl border border-[#C8960C]/30">
                    <i className={`${item.icon} text-xl text-[#8B2500]`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#8B2500] text-base mb-2">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── Section 3: Process ── */}
          <section id="process">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white flex-shrink-0">
                <i className="ri-route-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">Section 03</span>
                <h2 className="text-3xl font-bold text-[#8B2500]">Our Process</h2>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5 mb-10">
              <p className="text-sm leading-relaxed">
                Our approach to this engagement was structured around three parallel workstreams:
                licensing, property preparation, and operational setup. By running these concurrently
                where possible, we were able to compress the overall timeline significantly compared to
                a sequential approach. Here is how each phase unfolded.
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  phase: 'Phase 1',
                  title: 'Initial Consultation & Feasibility Assessment',
                  duration: 'Week 1',
                  icon: 'ri-search-eye-line',
                  steps: [
                    'Conducted a detailed on-site property inspection to assess structural compliance requirements',
                    'Reviewed zoning classification and confirmed eligibility for Ryokan (Simple Lodging) license in Ota City',
                    'Analyzed comparable Airbnb listings in the area to establish realistic revenue projections',
                    'Presented the owner with a clear licensing roadmap, cost estimate, and projected timeline',
                    'Agreed on scope: full licensing support plus ongoing Airbnb management',
                  ],
                },
                {
                  phase: 'Phase 2',
                  title: 'Fire Safety Compliance & Property Upgrades',
                  duration: 'Weeks 2–4',
                  icon: 'ri-fire-line',
                  steps: [
                    'Engaged a certified fire safety inspector for formal property assessment',
                    'Coordinated installation of smoke detectors (all rooms), fire extinguisher, emergency lighting, and evacuation signage',
                    'Arranged electrical panel inspection and obtained compliance certificate',
                    'Oversaw minor structural modifications to meet minimum room size and ventilation standards',
                    'Obtained official fire safety compliance certificate from Ota City fire department',
                  ],
                },
                {
                  phase: 'Phase 3',
                  title: 'Document Preparation & Application',
                  duration: 'Weeks 3–6',
                  icon: 'ri-file-list-3-line',
                  steps: [
                    'Obtained building registry (登記簿謄本) and land registry documents from the Legal Affairs Bureau',
                    'Commissioned professional floor plans meeting ward office specifications',
                    'Drafted operational procedures, emergency response protocols, and guest management policies in Japanese',
                    'Prepared complete application package for Ota City health department (保健所)',
                    'Submitted formal Ryokan license application with all supporting documents',
                  ],
                },
                {
                  phase: 'Phase 4',
                  title: 'Inspection & License Issuance',
                  duration: 'Weeks 7–10',
                  icon: 'ri-eye-line',
                  steps: [
                    'Coordinated on-site inspection with Ota City health department and fire safety officials',
                    'Accompanied inspectors during the visit and addressed all queries in real time',
                    'Resolved one minor deficiency (additional signage) within 48 hours of inspection',
                    'Received official Ryokan (Simple Lodging) license from Ota City',
                    'Registered license number on all booking platforms as required by law',
                  ],
                },
                {
                  phase: 'Phase 5',
                  title: 'Airbnb Setup & Launch',
                  duration: 'Weeks 10–14',
                  icon: 'ri-rocket-line',
                  steps: [
                    'Arranged professional interior photography and listing copywriting in English and Japanese',
                    'Created and optimized Airbnb listing with dynamic pricing strategy targeting Haneda transit guests',
                    'Installed smart lock system for contactless check-in and key management',
                    'Established cleaning team schedule and quality control protocols',
                    'Set up guest communication templates and 24/7 response system',
                    'Activated listing — first booking received within 72 hours of going live',
                  ],
                },
              ].map((phase, i) => (
                <div key={phase.phase} className="bg-white border border-[#C8960C]/20 rounded-xl overflow-hidden hover:-translate-y-2 hover:shadow-lg hover:border-[#8B2500] transition-all duration-300">
                  <div
                    className="flex items-center gap-4 px-6 py-4"
                    style={{ background: i === 4 ? 'linear-gradient(135deg, #8B2500, #C8960C)' : 'linear-gradient(135deg, #F5F1E8, #EDE0C4)' }}
                  >
                    <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${i === 4 ? 'bg-white/20' : 'bg-[#8B2500]'}`}>
                      <i className={`${phase.icon} text-xl ${i === 4 ? 'text-white' : 'text-white'}`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-bold uppercase tracking-widest ${i === 4 ? 'text-white/70' : 'text-[#C8960C]'}`}>{phase.phase}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${i === 4 ? 'bg-white/20 text-white' : 'bg-[#8B2500]/10 text-[#8B2500]'}`}>{phase.duration}</span>
                      </div>
                      <h3 className={`font-bold text-base ${i === 4 ? 'text-white' : 'text-[#8B2500]'}`}>{phase.title}</h3>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <ul className="space-y-2.5">
                      {phase.steps.map((step) => (
                        <li key={step} className="flex items-start gap-3 text-sm text-gray-600">
                          <i className="ri-checkbox-circle-fill text-[#C8960C] mt-0.5 flex-shrink-0"></i>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-[#FFF9E6] border-l-4 border-[#C8960C] p-6 rounded-r-xl">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong className="text-[#8B2500]">Key Insight:</strong> Running licensing and property
                preparation in parallel — rather than sequentially — saved approximately 6 weeks compared
                to a standard approach. This is only possible when you have direct experience with the
                process and know exactly what inspectors will look for before the formal application is
                submitted. This is the core advantage of working with an active operator rather than a
                theoretical consultant.
              </p>
            </div>
          </section>

          {/* ── Section 4: Launch Timeline ── */}
          <section id="timeline">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white flex-shrink-0">
                <i className="ri-calendar-check-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">Section 04</span>
                <h2 className="text-3xl font-bold text-[#8B2500]">Launch Timeline</h2>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-sm text-gray-700 leading-relaxed">
                From the date of our initial consultation to the owner's first confirmed Airbnb booking,
                the total elapsed time was <strong className="text-[#8B2500]">14 weeks (3.5 months)</strong>. This is significantly
                faster than the industry average of 6–12 months for first-time Ryokan license applicants
                in Tokyo. Below is the complete milestone timeline.
              </p>
            </div>

            {/* Visual Timeline */}
            <div className="relative overflow-x-auto">
              <div className="min-w-[640px]">
                {/* Timeline track */}
                <div className="relative flex items-start gap-0 mb-6">
                  {[
                    { label: 'Consultation', week: 'Week 1', icon: 'ri-discuss-line', color: '#8B2500' },
                    { label: 'Compliance Work', week: 'Weeks 2–4', icon: 'ri-tools-line', color: '#8B2500' },
                    { label: 'Application', week: 'Weeks 5–6', icon: 'ri-send-plane-line', color: '#8B2500' },
                    { label: 'Inspection', week: 'Weeks 7–10', icon: 'ri-eye-line', color: '#8B2500' },
                    { label: 'License Issued', week: 'Week 11', icon: 'ri-award-line', color: '#C8960C' },
                    { label: 'Listing Live', week: 'Week 13', icon: 'ri-airplay-line', color: '#C8960C' },
                    { label: 'First Booking', week: 'Week 14', icon: 'ri-star-fill', color: '#2d7a3a' },
                  ].map((item, i, arr) => (
                    <div key={item.label} className="flex-1 flex flex-col items-center relative">
                      {/* Connector line */}
                      {i < arr.length - 1 && (
                        <div
                          className="absolute top-5 left-1/2 w-full h-0.5 z-0"
                          style={{ background: 'linear-gradient(to right, #C8960C, #C8960C)', opacity: 0.3 }}
                        />
                      )}
                      {/* Node */}
                      <div
                        className="relative z-10 w-10 h-10 flex items-center justify-center rounded-full border-2 border-white shadow-md text-white"
                        style={{ backgroundColor: item.color }}
                      >
                        <i className={`${item.icon} text-sm`}></i>
                      </div>
                      <div className="mt-3 text-center px-1">
                        <div className="text-xs font-bold text-[#3d1f0a] leading-tight">{item.label}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{item.week}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Milestone Detail Cards */}
            <div className="mt-8 space-y-4">
              {[
                {
                  milestone: 'Preparation',
                  period: 'Weeks 1–6',
                  icon: 'ri-tools-line',
                  items: [
                    'Initial consultation and property feasibility assessment completed',
                    'Fire safety compliance work initiated and completed within 3 weeks',
                    'All required documents gathered and application package assembled',
                    'Formal Ryokan license application submitted to Ota City health department',
                  ],
                },
                {
                  milestone: 'Application & Approval',
                  period: 'Weeks 7–11',
                  icon: 'ri-government-line',
                  items: [
                    'On-site inspection conducted by Ota City health and fire officials',
                    'Minor deficiency (additional evacuation signage) resolved within 48 hours',
                    'No re-inspection required — application approved on first submission',
                    'Official Ryokan (Simple Lodging) license received from Ota City',
                  ],
                },
                {
                  milestone: 'Listing & First Booking',
                  period: 'Weeks 12–14',
                  icon: 'ri-star-line',
                  items: [
                    'Professional photography session completed — 28 high-quality listing photos',
                    'Airbnb listing created with bilingual descriptions and dynamic pricing',
                    'Smart lock installed; cleaning team onboarded and briefed',
                    'Listing activated — first booking confirmed within 72 hours of going live',
                  ],
                },
              ].map((block) => (
                <div key={block.milestone} className="bg-white border border-[#C8960C]/20 rounded-xl p-6 hover:-translate-y-2 hover:shadow-lg hover:border-[#8B2500] transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#8B2500] rounded-xl text-white flex-shrink-0">
                      <i className={`${block.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#8B2500] text-base">{block.milestone}</h3>
                      <span className="text-xs text-gray-400">{block.period}</span>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {block.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#C8960C] mt-0.5 flex-shrink-0"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* ── Section 5: Revenue Model ── */}
          <section id="revenue">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white flex-shrink-0">
                <i className="ri-money-yen-circle-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">Section 05</span>
                <h2 className="text-3xl font-bold text-[#8B2500]">Revenue Model</h2>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-sm text-gray-700 leading-relaxed">
                One of the most important questions any prospective operator asks is: "How much can I
                actually earn?" The honest answer depends on property size, location, license type,
                pricing strategy, and occupancy management. Below is the actual revenue model we
                developed for this Ota City property — based on real comparable data from our own
                active listings and the broader Ota City market.
              </p>
            </div>

            {/* Revenue Breakdown */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border-2 border-[#C8960C]/30 rounded-xl overflow-hidden">
                <div className="bg-[#F5F1E8] px-6 py-4 border-b border-[#C8960C]/20">
                  <h3 className="font-bold text-[#8B2500] text-base">Monthly Revenue Estimate</h3>
                  <p className="text-xs text-gray-500">Based on 65 m² property, Ota City, Ryokan license</p>
                </div>
                <div className="p-6 space-y-3">
                  {[
                    { scenario: 'Conservative (60% occupancy)', revenue: '¥140,000 – ¥160,000' },
                    { scenario: 'Realistic (75% occupancy)', revenue: '¥175,000 – ¥200,000', highlight: true },
                    { scenario: 'Peak Season (85%+ occupancy)', revenue: '¥220,000 – ¥260,000' },
                  ].map((row) => (
                    <div
                      key={row.scenario}
                      className={`flex justify-between items-center py-3 px-4 rounded-lg ${row.highlight ? 'bg-[#FFF5E6] border border-[#C8960C]/30' : 'bg-gray-50'}`}
                    >
                      <span className={`text-sm ${row.highlight ? 'font-semibold text-[#3d1f0a]' : 'text-gray-600'}`}>{row.scenario}</span>
                      <span className={`text-sm font-bold ${row.highlight ? 'text-[#8B2500]' : 'text-gray-700'}`}>{row.revenue}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-2 border-t border-[#C8960C]/20">
                    <p className="text-xs text-gray-400">* Gross revenue before management fees and operating costs</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-[#8B2500]/30 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-[#8B2500]/20" style={{ background: 'linear-gradient(135deg, #8B2500, #C8960C)' }}>
                  <h3 className="font-bold text-white text-base">Net Owner Income (Realistic)</h3>
                  <p className="text-xs text-white/70">After management fees and operating costs</p>
                </div>
                <div className="p-6 space-y-3">
                  {[
                    { item: 'Gross Monthly Revenue', value: '¥185,000', positive: true },
                    { item: 'Management Fee (20%)', value: '−¥37,000', positive: false },
                    { item: 'Cleaning Costs', value: '−¥18,000', positive: false },
                    { item: 'Platform Fees (Airbnb ~3%)', value: '−¥5,550', positive: false },
                    { item: 'Consumables & Maintenance', value: '−¥8,000', positive: false },
                  ].map((row) => (
                    <div key={row.item} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <span className="text-sm text-gray-600">{row.item}</span>
                      <span className={`text-sm font-semibold ${row.positive ? 'text-[#8B2500]' : 'text-gray-500'}`}>{row.value}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-[#8B2500]/30">
                    <span className="font-bold text-[#8B2500]">Net Owner Income</span>
                    <span className="font-bold text-[#8B2500] text-xl">≈ ¥116,450/mo</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Occupancy Strategy */}
            <div className="bg-[#F5F1E8] border border-[#C8960C]/30 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-[#8B2500] text-base mb-4 flex items-center gap-2">
                <i className="ri-bar-chart-line text-[#C8960C]"></i>
                Occupancy Strategy
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  {
                    icon: 'ri-flight-takeoff-line',
                    title: 'Haneda Transit Positioning',
                    desc: 'Ota City\'s proximity to Haneda Airport creates consistent demand from transit travelers — a segment with high booking frequency and lower price sensitivity than leisure tourists.',
                  },
                  {
                    icon: 'ri-price-tag-3-line',
                    title: 'Dynamic Pricing',
                    desc: 'Nightly rates adjusted based on local events, seasonal demand, and competitor availability. Peak rates (¥12,000–¥18,000/night) during Golden Week and cherry blossom season.',
                  },
                  {
                    icon: 'ri-star-line',
                    title: 'Review Velocity',
                    desc: 'Prioritizing early 5-star reviews through exceptional guest experience. Properties with 10+ reviews achieve 15–20% higher occupancy than new listings at equivalent price points.',
                  },
                ].map((item) => (
                  <div key={item.title} className="bg-white rounded-lg p-4 border border-[#C8960C]/15 hover:-translate-y-2 hover:shadow-lg transition-all duration-300">
                    <div className="w-11 h-11 flex items-center justify-center mb-4 rounded-xl" style={{ background: 'rgba(139,37,0,0.08)' }}>
                      <i className={`${item.icon} text-xl text-[#8B2500]`}></i>
                    </div>
                    <h4 className="font-bold text-[#8B2500] text-sm mb-2">{item.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Actual Results */}
            <div className="bg-gradient-to-r from-[#8B2500]/5 to-[#C8960C]/5 border border-[#C8960C]/30 rounded-xl p-6">
              <h3 className="font-bold text-[#8B2500] text-base mb-3 flex items-center gap-2">
                <i className="ri-trophy-line text-[#C8960C]"></i>
                Actual First 3 Months Results
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: '78%', label: 'Average Occupancy' },
                  { value: '¥182,000', label: 'Avg. Monthly Revenue' },
                  { value: '4.87 ★', label: 'Average Guest Rating' },
                  { value: '23', label: 'Total Bookings (3 mo)' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center bg-white rounded-lg p-4 border border-[#C8960C]/15">
                    <div className="text-2xl font-bold text-[#8B2500]">{stat.value}</div>
                    <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Section 6: Key Takeaways ── */}
          <section id="takeaways">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#8B2500] rounded-full text-white flex-shrink-0">
                <i className="ri-lightbulb-line text-xl"></i>
              </div>
              <div>
                <span className="text-xs font-bold text-[#C8960C] uppercase tracking-widest">Section 06</span>
                <h2 className="text-3xl font-bold text-[#8B2500]">Key Takeaways</h2>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-sm text-gray-700 leading-relaxed">
                This case study illustrates what is achievable when the licensing process is handled
                correctly from the start. The insights below are drawn directly from this engagement
                and reflect patterns we have observed consistently across our{' '}
                <Link to="/tokyo-airbnb-support" className="text-[#8B2500] font-semibold hover:underline">
                  Tokyo Minpaku support
                </Link>{' '}
                work in Ota City.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-10">
              {[
                {
                  num: '01',
                  title: 'License Type Determines Everything',
                  desc: 'The single most impactful decision in this case was choosing the Ryokan license over Minpaku. The 180-day cap under Minpaku would have limited annual revenue to approximately ¥900,000 — less than half of what the Ryokan license enables. If your goal is meaningful income, the Ryokan path is almost always the right choice.',
                },
                {
                  num: '02',
                  title: 'Location Advantage Must Be Leveraged',
                  desc: 'Ota City\'s proximity to Haneda Airport is a genuine competitive advantage — but only if your listing is positioned to capture transit demand. Generic "Tokyo apartment" listings miss this entirely. Targeted positioning to Haneda travelers drove 40% of all bookings in the first three months.',
                },
                {
                  num: '03',
                  title: 'Pre-Application Consultation Prevents Delays',
                  desc: 'The informal pre-consultation with the ward office (before formal application submission) is the single most underutilized step in the licensing process. It costs nothing, takes one hour, and prevents the document rejections and re-inspections that cause 6–12 month delays for unprepared applicants.',
                },
                {
                  num: '04',
                  title: 'Parallel Workstreams Compress Timelines',
                  desc: 'Running licensing and property preparation simultaneously — rather than waiting for the license before preparing the property — saved approximately 6 weeks. This requires confidence in the outcome, which comes from experience. First-time applicants typically cannot do this safely without guidance.',
                },
                {
                  num: '05',
                  title: 'First Reviews Are Disproportionately Valuable',
                  desc: 'The first 10 guest reviews have an outsized impact on long-term occupancy rates. We prioritized exceptional early guest experiences — including a welcome gift, bilingual house manual, and same-day response to all messages — to build review velocity quickly. The property reached Superhost status within 6 months.',
                },
                {
                  num: '06',
                  title: 'Full Outsourcing Is Viable — With the Right Partner',
                  desc: 'The owner\'s goal of zero personal involvement was fully achieved. From licensing through to ongoing management, the owner\'s only role is reviewing monthly revenue reports. This is only possible with a partner who has direct operational experience — not just administrative or legal knowledge.',
                },
              ].map((item) => (
                <div key={item.num} className="bg-white border border-[#C8960C]/20 rounded-xl p-6 hover:-translate-y-2 hover:shadow-lg hover:border-[#8B2500] transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl font-bold text-[#C8960C]/30 leading-none flex-shrink-0">{item.num}</span>
                    <div>
                      <h3 className="font-bold text-[#8B2500] text-base mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary Quote */}
            <div
              className="relative rounded-2xl p-8 text-center overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #F5F1E8 0%, #EDE0C4 100%)', border: '2px solid #C8960C' }}
            >
              <div className="absolute top-4 left-6 text-6xl text-[#C8960C]/20 font-serif leading-none">"</div>
              <div className="relative">
                <p className="text-base text-[#3d1f0a] leading-relaxed font-medium italic max-w-3xl mx-auto mb-4">
                  The key difference between a 3-month launch and a 12-month struggle is not the
                  complexity of the process — it is whether you have someone who has done it before
                  standing next to you at every step.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-8 h-0.5 bg-[#C8960C]"></div>
                  <span className="text-sm font-semibold text-[#8B2500]">From Application to Operation — Ota City, Tokyo</span>
                  <div className="w-8 h-0.5 bg-[#C8960C]"></div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Internal Links & CTA ── */}
          <section>
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              {[
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Tokyo Minpaku Support',
                  desc: 'Get the same end-to-end support for your property. Free 30-minute consultation.',
                  to: '/tokyo-airbnb-support',
                  label: 'Learn More →',
                },
                {
                  icon: 'ri-price-tag-3-line',
                  title: 'Pricing Plans',
                  desc: 'Transparent pricing for licensing support and full Airbnb management in Tokyo.',
                  to: '/airbnb-management-tokyo',
                  label: 'View Pricing →',
                },
                {
                  icon: 'ri-file-text-line',
                  title: 'Minpaku Registration Guide',
                  desc: 'Step-by-step guide to the full registration process in Tokyo.',
                  to: '/minpaku-registration-tokyo',
                  label: 'Read Guide →',
                },
              ].map((card) => (
                <Link
                  key={card.title}
                  to={card.to}
                  className="block bg-[#F5F1E8] border border-[#C8960C]/30 rounded-xl p-6 hover:-translate-y-2 hover:shadow-lg hover:border-[#8B2500] transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center mb-4 rounded-xl" style={{ background: 'rgba(139,37,0,0.08)' }}>
                    <i className={`${card.icon} text-xl text-[#8B2500]`}></i>
                  </div>
                  <h3 className="font-bold text-[#8B2500] text-base mb-2">{card.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{card.desc}</p>
                  <span className="text-sm font-semibold text-[#8B2500] group-hover:underline">{card.label}</span>
                </Link>
              ))}
            </div>

            {/* Final CTA */}
            <div
              className="relative rounded-2xl overflow-hidden p-12 text-center text-white"
              style={{ background: 'linear-gradient(135deg, #1a0a00 0%, #8B2500 50%, #C8960C 100%)' }}
            >
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Tokyo%20Ota%20City%20night%20aerial%20view%20warm%20golden%20lights%20residential%20district%20Haneda%20airport%20direction%20artistic%20abstract&width=800&height=400&seq=case-cta-bg-02&orientation=landscape')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="relative">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-white/10 rounded-full border border-white/20">
                  <i className="ri-home-smile-line text-3xl text-[#C8960C]"></i>
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  Is Your Property the Next Success Story?
                </h2>
                <p className="text-sm text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                  If you own a property in Ota City or anywhere in Tokyo, we can assess its potential
                  and guide you through the complete licensing and launch process — just as we did here.
                  Book your free 30-minute consultation today.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/tokyo-airbnb-support#contact"
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#8B2500] px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 hover:-translate-y-1 hover:shadow-xl active:translate-y-0 whitespace-nowrap cursor-pointer"
                    style={{ boxShadow: '0 6px 20px rgba(0,0,0,0.18)' }}
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </Link>
                  <Link
                    to="/airbnb-management-tokyo"
                    className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 hover:-translate-y-1 hover:bg-white/15 hover:shadow-xl active:translate-y-0 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-price-tag-3-line text-lg"></i>
                    View Pricing Plans
                  </Link>
                </div>
                <p className="mt-6 text-sm text-white/50">
                  Tokyo Minpaku Support · Airbnb Management Tokyo · Ryokan License Application Tokyo · Ota City Short-Term Rental Support
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* ── Inline Consultation Form ── */}
        <section className="py-20 bg-[#F5F1E8]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#8B2500]/10 border border-[#8B2500]/20 text-[#8B2500] text-xs font-bold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
                <i className="ri-calendar-line"></i>
                Free Consultation
              </div>
              <h2 className="text-3xl font-bold text-[#8B2500] mb-3">
                無料相談を予約する
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed max-w-xl mx-auto">
                このケーススタディのように、あなたの物件でも同じ成果を目指せます。<br />
                まずは30分の無料相談から、可能性を一緒に確認しましょう。
              </p>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[
                { icon: 'ri-time-line', label: '24時間以内に返信' },
                { icon: 'ri-shield-check-line', label: '完全無料・押し付けなし' },
                { icon: 'ri-home-smile-line', label: '現役ホストが対応' },
                { icon: 'ri-map-pin-line', label: '大田区拠点' },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-1.5 bg-white border border-[#C8960C]/30 rounded-full px-3 py-1.5 text-xs text-[#3d1f0a] font-medium">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${b.icon} text-[#8B2500]`}></i>
                  </div>
                  {b.label}
                </div>
              ))}
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl border border-[#C8960C]/30 shadow-lg overflow-hidden">
              <div
                className="px-8 py-5 border-b border-[#C8960C]/20"
                style={{ background: 'linear-gradient(135deg, #1a0a00 0%, #8B2500 100%)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full border border-white/20">
                    <i className="ri-discuss-line text-xl text-[#C8960C]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-base">無料30分相談フォーム</h3>
                    <p className="text-xs text-white/60">Book Your Free 30-Min Consultation</p>
                  </div>
                </div>
              </div>

              {formState === 'success' ? (
                <div className="px-8 py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4 border-2 border-green-200">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#8B2500] mb-2">送信完了しました！</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    お問い合わせありがとうございます。<br />
                    24時間以内にご連絡いたします。
                  </p>
                  <button
                    onClick={() => setFormState('idle')}
                    className="mt-6 text-sm text-[#8B2500] underline cursor-pointer"
                  >
                    別のお問い合わせをする
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="case-study-consultation-form"
                  onSubmit={handleSubmit}
                  className="px-8 py-8 space-y-5"
                >
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold tracking-wide uppercase mb-1.5" style={{ color: '#3d1f0a' }}>
                        お名前 <span className="text-[#8B2500]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="山田 太郎"
                        onChange={handleFieldChange}
                        className="w-full rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-[#a08060] focus:outline-none transition-all"
                        style={{
                          border: fieldErrors.name ? '1px solid rgba(220,38,38,0.6)' : '1px solid rgba(200,150,12,0.3)',
                          boxShadow: fieldErrors.name ? '0 0 0 3px rgba(220,38,38,0.08)' : 'none',
                        }}
                        onFocus={e => {
                          e.target.style.borderColor = fieldErrors.name ? 'rgba(220,38,38,0.8)' : '#8B2500';
                          e.target.style.boxShadow = fieldErrors.name ? '0 0 0 3px rgba(220,38,38,0.12)' : '0 0 0 3px rgba(139,37,0,0.1)';
                        }}
                        onBlur={e => {
                          e.target.style.borderColor = fieldErrors.name ? 'rgba(220,38,38,0.6)' : 'rgba(200,150,12,0.3)';
                          e.target.style.boxShadow = fieldErrors.name ? '0 0 0 3px rgba(220,38,38,0.08)' : 'none';
                        }}
                      />
                      {fieldErrors.name && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-error-warning-fill text-xs" style={{ color: '#dc2626' }}></i>
                          </div>
                          <p className="text-xs" style={{ color: '#dc2626' }}>{fieldErrors.name}</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-wide uppercase mb-1.5" style={{ color: '#3d1f0a' }}>
                        メールアドレス <span className="text-[#8B2500]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        onChange={handleFieldChange}
                        className="w-full rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-[#a08060] focus:outline-none transition-all"
                        style={{
                          border: fieldErrors.email ? '1px solid rgba(220,38,38,0.6)' : '1px solid rgba(200,150,12,0.3)',
                          boxShadow: fieldErrors.email ? '0 0 0 3px rgba(220,38,38,0.08)' : 'none',
                        }}
                        onFocus={e => {
                          e.target.style.borderColor = fieldErrors.email ? 'rgba(220,38,38,0.8)' : '#8B2500';
                          e.target.style.boxShadow = fieldErrors.email ? '0 0 0 3px rgba(220,38,38,0.12)' : '0 0 0 3px rgba(139,37,0,0.1)';
                        }}
                        onBlur={e => {
                          e.target.style.borderColor = fieldErrors.email ? 'rgba(220,38,38,0.6)' : 'rgba(200,150,12,0.3)';
                          e.target.style.boxShadow = fieldErrors.email ? '0 0 0 3px rgba(220,38,38,0.08)' : 'none';
                        }}
                      />
                      {fieldErrors.email && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-error-warning-fill text-xs" style={{ color: '#dc2626' }}></i>
                          </div>
                          <p className="text-xs" style={{ color: '#dc2626' }}>{fieldErrors.email}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-wide uppercase mb-1.5" style={{ color: '#3d1f0a' }}>
                      物件の所在地
                    </label>
                    <input
                      type="text"
                      name="location"
                      placeholder="例：東京都大田区"
                      className="w-full border border-[#C8960C]/30 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-[#a08060] focus:outline-none focus:border-[#8B2500] focus:ring-1 focus:ring-[#8B2500]/20 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-wide uppercase mb-1.5" style={{ color: '#3d1f0a' }}>
                      ご相談内容
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="物件の状況やご希望をお気軽にご記入ください（500文字以内）"
                      className="w-full border border-[#C8960C]/30 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-[#a08060] focus:outline-none focus:border-[#8B2500] focus:ring-1 focus:ring-[#8B2500]/20 transition-all resize-none"
                    />
                  </div>

                  {formState === 'error' && (
                    <div
                      className="flex items-start gap-3 px-4 py-3.5 rounded-lg"
                      style={{
                        background: 'rgba(220,38,38,0.07)',
                        border: '1px solid rgba(220,38,38,0.28)',
                      }}
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-fill text-base" style={{ color: '#dc2626' }}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold leading-snug" style={{ color: '#b91c1c' }}>
                          送信に失敗しました
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: 'rgba(185,28,28,0.75)' }}>
                          もう一度お試しください。
                        </p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-base text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    style={{ background: 'linear-gradient(135deg, #8B2500, #C8960C)', boxShadow: '0 6px 20px rgba(139,37,0,0.4)' }}
                  >
                    {formState === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        送信中...
                      </>
                    ) : (
                      <>
                        <i className="ri-calendar-check-line text-lg"></i>
                        無料相談を予約する
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-gray-400">
                    送信後24時間以内にご連絡いたします。完全無料・勧誘なし。
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

      </article>

      <Footer />
    </div>
  );
}
